import  { useState, useEffect, useRef } from "react";
import { Modal } from "@mui/material";
import { ClipLoader } from "react-spinners";
import { FaCheckCircle } from "react-icons/fa";
import { RxCopy } from "react-icons/rx";
import useWireTransfer from "../../../hooks/invoice/useWireTransfer";
import { showToast } from "../../../utils/Toast";
import logo from "../../../assets/lux-logo/bidcaribbeansBlueLogo.jpg";
import { useWireTransferContext } from "../../../context/WireTransferContext";

const WireTransferInvoice = ({
  isOpen,
  onClose,
  fetchFunds,
  existingRefId = "",
}) => {
  const [amount, setAmount] = useState("");
  const [paymentProof, setPaymentProof] = useState(null);
  const [amountError, setAmountError] = useState("");
  const [proofError, setProofError] = useState("");
  const fileInputRef = useRef(null);

  const {
    success,
    loading,
    error,
    submitWirePayment,
    generateReferenceNumber,
    resetWireTransferState,
  } = useWireTransfer();

  const [localReferenceNumber, setLocalReferenceNumber] =
    useState(existingRefId);
  const { refreshWireTransfers } = useWireTransferContext();

  useEffect(() => {
    if (isOpen) {
      if (existingRefId) {
        setLocalReferenceNumber(existingRefId);
      } else {
        const ref = generateReferenceNumber();
        setLocalReferenceNumber(ref);
      }
      resetForm();
    }
  }, [isOpen, existingRefId]);

  const resetForm = () => {
    setAmount("");
    setPaymentProof(null);
    setAmountError("");
    setProofError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    resetForm();
    if (resetWireTransferState) {
      resetWireTransferState();
    }
    if (fetchFunds) {
      fetchFunds();
    }
    onClose();
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (!/^\d*\.?\d*$/.test(value)) return;
    setAmount(value);
    if (parseFloat(value) <= 0) {
      setAmountError("Amount must be greater than 0");
    } else {
      setAmountError("");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setProofError("File size should be less than 10MB");
        setPaymentProof(null);
        return;
      }
      if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
        setProofError("Only JPG, JPEG or PNG files are allowed");
        setPaymentProof(null);
        return;
      }

      setPaymentProof(file);
      setProofError("");
    } else {
      setPaymentProof(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let isValid = true;

    if (!amount || parseFloat(amount) <= 0) {
      setAmountError("Please enter a valid amount");
      isValid = false;
    }

    if (!paymentProof) {
      setProofError("Please upload payment proof");
      isValid = false;
    }

    if (!isValid) return;

    try {
      await submitWirePayment({
        depositAmount: amount,
        paymentProof: paymentProof,
        referenceId: localReferenceNumber,
      });

      if (typeof refreshWireTransfers === "function") {
        refreshWireTransfers();
      }
    } catch (err) {
      // console.error("Error submitting form:", err);
    }
  };

  const copyReferenceNumber = () => {
    navigator.clipboard
      .writeText(localReferenceNumber)
      .then(() => {
        showToast("Reference number copied to clipboard", "success");
      })
      .catch((err) => {
        // console.error("Failed to copy: ", err);
        showToast("Failed to copy reference number", "error");
      });
  };

  return (
    <Modal open={isOpen} onClose={!loading ? handleClose : null}>
      <div className="bg-white p-6 lg:m-0 m-2 rounded-xl shadow-lg w-full lg:w-[700px] max-h-[90vh] overflow-y-auto absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        {/* Logo Header */}
        <div className="bg-[#008b98] p-4 rounded-xl flex justify-center">
          <img src={logo} alt="Company Logo" className="w-28 h-auto" />
        </div>

        {/* Content */}
        <div className="text-center py-6">
          {!success ? (
            <>
              <h1 className="text-2xl font-bold text-gray-800 mb-4">
                Wire Transfer Invoice
              </h1>
              <p className="text-gray-600 text-sm mb-6">
                Please fill in the details below to submit your wire transfer
              </p>

              <form onSubmit={handleSubmit} className="mt-4">
                {/* Reference Number */}
                <div className="mb-4">
                  <label className="block text-left text-gray-700 font-semibold mb-2">
                    Reference Number
                  </label>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={localReferenceNumber}
                      className="w-full p-2 border rounded-md bg-gray-100"
                      disabled
                    />
                    <button
                      type="button"
                      onClick={copyReferenceNumber}
                      className="ml-2 p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                      title="Copy reference number"
                    >
                      <RxCopy className="text-lg" />
                    </button>
                  </div>
                  <p className="text-xs text-left text-gray-500 mt-1">
                    Auto-generated reference number
                  </p>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <label className="block text-left text-gray-700 font-semibold mb-2">
                    Amount (BSD)
                  </label>
                  <input
                    type="text"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="Enter amount"
                    className={`w-full p-2 border rounded-md ${amountError ? "border-red-500" : ""}`}
                  />
                  {amountError && (
                    <p className="text-red-500 text-left text-xs mt-1">
                      {amountError}
                    </p>
                  )}
                </div>

                {/* Payment Proof */}
                <div className="mb-4">
                  <label className="block text-left text-gray-700 font-semibold mb-2">
                    Payment Proof
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/png, image/jpeg, image/jpg"
                    className={`w-full p-2 border rounded-md ${proofError ? "border-red-500" : ""}`}
                  />
                  {proofError && (
                    <p className="text-red-500 text-left text-xs mt-1">
                      {proofError}
                    </p>
                  )}
                  <p className="text-xs text-left text-gray-500 mt-1">
                    Upload a screenshot or photo of your payment confirmation
                  </p>
                  {paymentProof && (
                    <p className="text-xs text-left text-green-500 mt-1">
                      File selected: {paymentProof.name}
                    </p>
                  )}
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
                    {error}
                  </div>
                )}

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={loading}
                    className={`px-5 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500 transition ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-5 py-2 bg-primary-red text-white rounded-md hover:bg-primary-red/90 transition flex items-center justify-center ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {loading ? (
                      <ClipLoader size={20} color="#fff" />
                    ) : (
                      "Submit Receipt"
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center p-6">
              <FaCheckCircle className="text-green-500 text-6xl mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-green-600 mb-2">
                Success!
              </h2>
              <p className="text-gray-600 mb-4">
                Your wire transfer has been submitted successfully.
              </p>

              <p className="text-gray-600 mb-4">
                <span className="font-bold">Note:</span> Payments are usually
                verified within 24–48 business hours once proof is submitted.
              </p>
              <div className="flex items-center justify-center mb-6">
                <p className="text-gray-600 mr-2">
                  Reference Number:{" "}
                  <span className="font-semibold">{localReferenceNumber}</span>
                </p>
                <button
                  onClick={copyReferenceNumber}
                  className=""
                  title="Copy reference number"
                >
                  <RxCopy className="text-lg text-gray-600 " />
                </button>
              </div>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default WireTransferInvoice;
